import { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { StepModel, StepColor, StepUpgrade, preloadImages } from "@/components/swing_doors/SwingSteps";
import { QuoteReveal } from "@/components/swing_doors/SwingContactAndQuote";
import { Hero, PackageCards, PACKAGES, StepQtyMesh, StepContactLite } from "@/components/swing_doors/SwingPackages";
import { getAttribution } from "@/utils/attribution";

const BACKEND_URL = window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const FUNNEL = "v5_swing_packages";
const SOURCE = "swinging-door-packages";
const fmt2 = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);
const PKG_STEPS = ["package", "color", "qty", "contact"];
const DEFAULT_PKG = PACKAGES.find((p) => p.featured);
const GRID_STEPS = ["package", "color", "upgrade", "qty", "contact"];

const utm = () => {
  const p = new URLSearchParams(window.location.search);
  return Object.fromEntries(["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "gclid", "fbclid"].map((k) => [k, p.get(k)]));
};

export default function SwingingDoorPackages() {
  const [catalog, setCatalog] = useState(null);
  const [step, setStep] = useState(0);
  const [pkg, setPkg] = useState(DEFAULT_PKG);
  const [showAll, setShowAll] = useState(false);
  const [path, setPath] = useState("package");
  const [modelKey, setModelKey] = useState(DEFAULT_PKG.model);
  const [upgradeKey, setUpgradeKey] = useState(DEFAULT_PKG.upgrade);
  const [color, setColor] = useState("white");
  const [qty, setQty] = useState(1);
  const [contact, setContact] = useState({ name: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const [leadId, setLeadId] = useState(null);
  const [serverQuote, setServerQuote] = useState(null);
  const submissionId = useRef(null);
  const [sessionId] = useState(() => `swp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);
  const steps = path === "grid" ? GRID_STEPS : PKG_STEPS;
  const stepName = steps[step];

  const track = (step_name, extra = {}) =>
    axios.post(`${API}/funnel/track`, { session_id: sessionId, step: steps.indexOf(step_name), step_name, event_type: step_name, funnel_version: FUNNEL, ...extra }).catch(() => {});

  useEffect(() => {
    axios.get(`${API}/swing-doors/catalog`).then((r) => {
      setCatalog(r.data);
      preloadImages(r.data.upgrades.map((u) => u.image_url));
    }).catch(() => toast.error("Couldn't load door styles. Please refresh."));
    const u = utm();
    track("visit", { utm_source: u.utm_source, utm_medium: u.utm_medium, utm_campaign: u.utm_campaign, metadata: { referrer: document.referrer || null, landing_page: window.location.pathname } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { window.scrollTo({ top: 0 }); }, [step]);

  const model = catalog?.models.find((m) => m.key === modelKey);
  const upgrade = catalog?.upgrades.find((u) => u.key === upgradeKey);
  const colorLabel = catalog?.colors.find((c) => c.id === color)?.label;

  const quote = useMemo(() => {
    if (!model) return { items: [], subtotal: 0, tax: 0, total: 0, qty };
    const items = [{ name: `${model.name} (${colorLabel})`, quantity: qty, line_total: model.price * qty, taxable: model.is_taxed }];
    if (upgrade) items.push({ name: upgrade.name.replace(" (Swinging Door)", ""), quantity: qty, line_total: upgrade.price * qty, taxable: upgrade.is_taxed });
    const subtotal = items.reduce((s, i) => s + i.line_total, 0);
    const tax = Math.round(items.filter((i) => i.taxable).reduce((s, i) => s + i.line_total, 0) * catalog.tax_rate * 100) / 100;
    return { items, subtotal, tax, total: subtotal + tax, qty };
  }, [model, upgrade, colorLabel, qty, catalog]);

  const choosePackage = (p) => {
    setPkg(p); setPath("package"); setModelKey(p.model); setUpgradeKey(p.upgrade);
    track("package_chosen", { metadata: { package: p.id } });
    setTimeout(() => setStep(1), 150);
  };
  const pickModel = (key) => {
    setPkg(null); setPath("grid"); setModelKey(key); setUpgradeKey(null);
    track("style_chosen", { metadata: { model_key: key } });
    setTimeout(() => setStep(1), 150);
  };

  const submit = async () => {
    if (!contact.name.trim()) return toast.error("First name is required");
    if (contact.phone.replace(/\D/g, "").length !== 10) return toast.error("Enter a valid 10-digit mobile number");
    if (leadId) return;
    submissionId.current ||= window.crypto?.randomUUID?.() || `sub_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/intake/submit`, {
        service_type: "swing_door", screen_count: qty, screen_type: `swing_${modelKey}_${color}`,
        full_name: contact.name.trim(), phone: contact.phone.trim(), email: "", address: "", zip_code: "",
        funnel_version: FUNNEL, funnel_session_id: sessionId, source: SOURCE, ...getAttribution(), submission_id: submissionId.current,
        ...utm(), referrer: document.referrer || null, landing_page: "/swinging-door-packages",
        estimate_data: { model_key: modelKey, color, upgrade_key: upgradeKey, qty, package: pkg?.id || null },
      });
      const saved = data.estimate_data;
      if (saved?.line_items) setServerQuote({ items: saved.line_items, subtotal: saved.subtotal, tax: saved.tax, total: saved.total, discount: saved.discount, discountName: saved.discount_name, qty });
      setLeadId(data.lead_id);
      track("quote_revealed", { step: 99, metadata: { lead_id: data.lead_id, quote_value: quote.total, package: pkg?.id || null } });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const next = () => (stepName === "package" ? (pkg ? choosePackage(pkg) : setStep(1)) : setStep(step + 1));

  if (leadId) return <QuoteReveal quote={serverQuote || quote} model={model} colorLabel={colorLabel} upgradeName={upgrade ? upgrade.name.replace(" (Swinging Door)", "") : "Standard Screen"} name={contact.name} leadId={leadId} />;

  const meshName = upgrade ? upgrade.name.replace(" Upgrade (Swinging Door)", "") : "Standard screen";
  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-50 to-white pb-36" data-testid="swing-packages-funnel">
      <header className="max-w-6xl mx-auto px-4 pt-5 pb-3 flex flex-col items-center gap-1.5">
        <img src="https://customer-assets.emergentagent.com/job_tech-dispatch-31/artifacts/a6ta9f48_SCREENZZ-LOGO-RGB_TextOnly.png" alt="ScreenzZ" className="h-10 sm:h-12 w-auto" />
        <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.18em] text-[#207024] font-semibold">Swinging Screen Doors</div>
      </header>
      <div className={`${step === 0 ? "max-w-6xl" : "max-w-3xl"} mx-auto px-4 mb-4`}>
        <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden">
          <div className="h-full bg-[#207024] transition-[width] duration-300" style={{ width: `${((step + 1) / steps.length) * 100}%` }} />
        </div>
        <div className="text-[11px] text-zinc-500 mt-1.5 flex justify-between">
          {step > 0 ? (
            <button onClick={() => setStep(step - 1)} className="text-[#207024] font-semibold flex items-center gap-1" data-testid="swing-back-btn"><ChevronLeft className="w-3 h-3" /> Back</button>
          ) : <span />}
          <span data-testid="swing-step-label">Step {step + 1} of {steps.length}</span>
        </div>
      </div>
      <main className={`${step === 0 ? "max-w-6xl" : "max-w-3xl"} mx-auto px-4`}>
        {!catalog ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#207024]" /></div>
        ) : (
          <>
            {stepName === "package" && (
              <div className="space-y-8">
                <Hero />
                <PackageCards catalog={catalog} selectedId={pkg?.id} onChoose={choosePackage} onShowAll={() => setShowAll(!showAll)} />
                {showAll && <div data-testid="swing-all-styles"><StepModel models={catalog.models} modelKey={path === "grid" ? modelKey : null} onPick={pickModel} /></div>}
              </div>
            )}
            {stepName === "color" && <StepColor colors={catalog.colors} color={color} setColor={setColor} />}
            {stepName === "upgrade" && <StepUpgrade upgrades={catalog.upgrades} upgradeKey={upgradeKey} setUpgradeKey={setUpgradeKey} />}
            {stepName === "qty" && <StepQtyMesh qty={qty} setQty={setQty} upgrades={catalog.upgrades} upgradeKey={upgradeKey} setUpgradeKey={setUpgradeKey} showMesh={path === "package"} />}
            {stepName === "contact" && <StepContactLite c={contact} set={{ name: (v) => setContact((c) => ({ ...c, name: v })), phone: (v) => setContact((c) => ({ ...c, phone: v })) }} submitting={submitting} onSubmit={submit} />}
          </>
        )}
      </main>
      {catalog && model && (
        <footer className="fixed bottom-0 inset-x-0 bg-white border-t-2 border-zinc-200 shadow-lg">
          <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
            <div className="min-w-0" data-testid="swing-order-summary">
              <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold truncate">{pkg ? `${pkg.name} package` : "Your door"}</div>
              <div className="text-sm text-zinc-800 truncate"><span className="font-semibold text-[#207024]">{qty}×</span> {model.name} · {colorLabel} · {meshName}</div>
              <div className="text-sm font-bold text-zinc-900" data-testid="swing-running-total">
                {fmt2(quote.total)} <span className="text-[11px] font-normal text-zinc-500">total incl. {fmt2(quote.tax)} tax</span>
              </div>
            </div>
            {stepName !== "contact" && (
              <button onClick={next} data-testid="swing-continue-btn" className="h-12 px-5 rounded-lg bg-[#207024] hover:bg-[#1a5c1d] text-white font-bold flex items-center gap-2 shrink-0 transition-colors">
                Continue <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </footer>
      )}
    </div>
  );
}
